import axios from 'axios'
import { getEnv } from '@utils/env'
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '@services/auth/accessTokenStore'

const URL = ''
export const AUTH_REQUEST_TIMEOUT_MS = 30000

// Global logout callback - will be set by the store
let globalLogoutCallback: ((expiredRefreshToken: boolean) => void) | null = null

// Function to set the logout callback from the store
export const setGlobalLogoutCallback = (
  callback: (expiredRefreshToken: boolean) => void,
): void => {
  globalLogoutCallback = callback
}

const axiosInstance = axios.create({
  baseURL: URL,
  timeout: 100000,
  headers: { 'Content-Type': 'application/json' },
})

const hasRequestHeader = (
  headers: Record<string, unknown> | undefined,
  name: string,
): boolean => {
  if (!headers) return false
  const target = name.toLowerCase()
  return Object.keys(headers).some(
    (key) => key.toLowerCase() === target && headers[key] != null,
  )
}

axiosInstance.interceptors.request.use(
  (config) => {
    // Only attach Bearer token if Authorization header is not already provided
    if (!config.headers['Authorization']) {
      const accessToken = getAccessToken()
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`
      }
    }

    const clinicId = localStorage.getItem('clinic_id')
    if (clinicId && !hasRequestHeader(config.headers, 'x-clinic-id')) {
      config.headers['X-Clinic-ID'] = clinicId
    }

    const tenantId = localStorage.getItem('tenant_id')
    if (tenantId && !hasRequestHeader(config.headers, 'x-tenant-id')) {
      config.headers['X-Tenant-ID'] = tenantId
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Global variables to track refresh state and queue pending requests
let isRefreshing = false
let refreshSubscribers: {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}[] = []

// Function to subscribe to the new token
const subscribeToTokenRefresh = (
  resolve: (token: string) => void,
  reject: (error: unknown) => void,
): void => {
  refreshSubscribers.push({ resolve, reject })
}

// Notify all subscribers with the new token
const onRefreshed = (newToken: string): void => {
  refreshSubscribers.forEach(({ resolve }) => resolve(newToken))
  refreshSubscribers = []
}

/**
 * Refresh access token using HttpOnly refresh cookie.
 * Uses a bare axios client (not this instance) to avoid interceptor recursion.
 * Returns the new access token and rejects if refresh fails or times out.
 */
export const refreshAccessTokenViaCookie = async (): Promise<string | null> => {
  const API_DOMAIN = getEnv('REACT_APP_AUTH_API_DOMAIN')
  const CLIENT_ID = getEnv('REACT_APP_CLIENT_ID', '')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (CLIENT_ID) {
    headers['X-Client-Authorization'] = `Basic ${btoa(`${CLIENT_ID}:`)}`
  }

  const { data } = await axios.post(
    `${API_DOMAIN}/api/v1/auth/token?grant_type=refresh_token`,
    {},
    {
      headers,
      withCredentials: true,
      timeout: AUTH_REQUEST_TIMEOUT_MS,
      timeoutErrorMessage: 'Session refresh timed out. Please sign in again.',
    },
  )

  const access_token = data?.data?.access_token as string | undefined
  if (!access_token) {
    throw new Error('No access token in refresh response')
  }

  setAccessToken(access_token)
  return access_token
}

// Axios response interceptor
// REFRESH TOKEN
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const requestUrl = String(originalRequest?.url ?? '')
    const isAuthTokenEndpoint = requestUrl.includes('/auth/token')
    const isPublicInvitationAuth =
      requestUrl.includes('/otp/invitation/') ||
      requestUrl.includes('/otp/verify')
    const isStandaloneFormsPath =
      typeof window !== 'undefined' &&
      (window.location.pathname === '/forms/public' ||
        window.location.pathname === '/forms/kiosk')

    // Check if error is due to unauthorized access (401)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthTokenEndpoint &&
      !isPublicInvitationAuth &&
      !isStandaloneFormsPath
    ) {
      originalRequest._retry = true

      // If a refresh request is already in progress, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeToTokenRefresh((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            resolve(axiosInstance(originalRequest))
          }, reject)
        })
      }

      isRefreshing = true

      try {
        const access_token = await refreshAccessTokenViaCookie()
        if (!access_token) {
          throw new Error('No refresh token available')
        }

        // Notify all queued requests about the new token
        onRefreshed(access_token)

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`
        return axiosInstance(originalRequest)
      } catch (refreshError: any) {
        clearAccessToken()
        // Settle every waiting request even when refresh fails.
        refreshSubscribers.forEach(({ reject }) => reject(refreshError))
        refreshSubscribers = []
        const { status } = refreshError.response || {}
        if (status === 401) {
          if (globalLogoutCallback) {
            globalLogoutCallback(true)
          }
        }
        // If refresh fails, reject all queued requests and handle the error
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false // Reset the refreshing state
      }
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
